import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import { Alert, Box, Button, Link, Stack, TextField } from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { acquisitionApi } from "../../api/acquisition";
import { acquisitionSessionId, acquisitionVisitorId } from "../../utils/acquisitionFunnel";
import {
  AuthPageShell,
  AuthPasswordField,
  authLinkSx,
  authPrimaryButtonSx,
} from "../../components/dashboard/auth/AuthFormShell";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [form, setForm] = React.useState({ email: "", password: "" });
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const message = location.state?.message || "";
  const messageSeverity = location.state?.messageSeverity || "success";
  const referralCode = String(searchParams.get("ref") || "").trim().toUpperCase();
  const registerHref = referralCode
    ? `/registro?ref=${encodeURIComponent(referralCode)}`
    : "/registro";

  const fromLocation = location.state?.from;
  const from = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search || ""}${fromLocation.hash || ""}`
    : "/dashboard";

  const handleChange = (event) => {
    setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const email = String(form.email || "").trim().toLowerCase();

    if (!email || !email.includes("@")) {
      setError("Ingresá un email válido.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      await login({ email, password: form.password });

      // BLOCK 4 · Signed-out returning clients must not stay in acquisition.
      // STATUS: STATIC_PASS · STAGING_PENDING. Reclassifying the same browser
      // session after a successful login removes existing clients from new-user
      // onboarding metrics without making telemetry part of the auth critical path.
      acquisitionApi.updateSession(acquisitionSessionId(), {
        visitorId: acquisitionVisitorId(),
        audience: "authenticated",
      }).catch(() => null);

      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell
      title="Ingresar"
      subtitle="Accedé a tu panel de WebProfit."
      footer={
        <>
          ¿No tenés cuenta?{" "}
          <Link component={RouterLink} to={registerHref} sx={authLinkSx}>
            Crear cuenta
          </Link>
        </>
      }
    >
      {message ? <Alert severity={messageSeverity}>{message}</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={1.5}>
          <TextField
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            fullWidth
            disabled={loading}
            autoFocus
            autoComplete="username"
            inputProps={{ inputMode: "email", autoCapitalize: "none", spellCheck: "false" }}
          />
          <AuthPasswordField
            label="Contraseña"
            name="password"
            value={form.password}
            onChange={handleChange}
            autoComplete="current-password"
            disabled={loading}
            inputProps={{ minLength: undefined }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
            <Link
              component={RouterLink}
              to="/olvide-contrasena"
              sx={{ ...authLinkSx, minHeight: 44, display: "inline-flex", alignItems: "center", fontSize: ".82rem" }}
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </Box>
          <Button type="submit" disabled={loading} variant="contained" size="large" startIcon={loading ? <WebProfitSpinner size={17} /> : undefined} sx={authPrimaryButtonSx}>
            {loading ? "Ingresando..." : "Ingresar"}
          </Button>
        </Stack>
      </Box>
    </AuthPageShell>
  );
}
